import React, { useState, useEffect, useMemo } from "react";
import "./Keyboard.less";
import ResizableTextarea from "./ResizableTextarea"; 

export type Key = {
  label: string;
  code: string;
  color?: string;
  width?: number;
};

export type KeyStroke = {
  code: string;
  shift?: boolean;
  shiftCode?: "ShiftLeft" | "ShiftRight";
  display: string;
};

type ShiftCode = NonNullable<KeyStroke["shiftCode"]>;

type KeyboardProps = {
  value: string; // 输入的文本
  height: number;
  onKeyPress: (key: Key, isCorrect: boolean | null) => void; 
  onChange: (value: string) => void; // 输入变化时的回调
  onComposingChnage?: (value: string) => void; // 输入法变化时的回调
  targetKeyCode?: KeyStroke | string; // 当前需要按下的键（可选）
  showKeyboard?: boolean; // 是否显示键盘（可选）
  availableKeyCodes?: string[]; // 可用按键范围（可选）
  showAvailableKeys?: boolean; // 是否显示可用按键范围
  showTargetKey?: boolean; // 是否显示当前需要按下的键
  onInit?: (ele: HTMLTextAreaElement) => void; // 初始化回调
  onHeightChange?: (height: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

const leftFingers = ["LP", "LR", "LM", "LI", "LT"];

const FINGERS = ["LP", "LR", "LM", "LI", "LT", "RT", "RI", "RM", "RR", "RP"];
const fingerHighlightColor = "rgba(37, 99, 235, 0.85)";

// 更完整的美式键盘指法映射
const keyToFingerMap: Record<string, string> = {
  Backquote: "LP",
  Digit1: "LP",
  Digit2: "LR",
  Digit3: "LM",
  Digit4: "LI",
  Digit5: "LI",
  Digit6: "RI",
  Digit7: "RI",
  Digit8: "RM",
  Digit9: "RR",
  Digit0: "RP",
  Minus: "RP",
  Equal: "RP",
  Tab: "LP",
  KeyQ: "LP", KeyA: "LP", KeyZ: "LP",
  KeyW: "LR", KeyS: "LR", KeyX: "LR",
  KeyE: "LM", KeyD: "LM", KeyC: "LM",
  KeyR: "LI", KeyT: "LI", KeyF: "LI", KeyG: "LI", KeyV: "LI", KeyB: "LI",
  KeyY: "RI", KeyU: "RI", KeyH: "RI", KeyJ: "RI", KeyN: "RI", KeyM: "RI",
  KeyI: "RM", KeyK: "RM",
  KeyO: "RR", KeyL: "RR", Comma: "RM",
  KeyP: "RP", BracketLeft: "RP", BracketRight: "RP", Backslash: "RP",
  Semicolon: "RP", Quote: "RP", Period: "RR", Slash: "RP",
  CapsLock: "LP",
  Enter: "RP",
  Space: "RT",
  ShiftLeft: "LP",
  ShiftRight: "RP",
  Backspace: "RP",
};

// 手指固定停靠的主 Home 键 (用来摆放代表十个指头的 SVG)
const fingerHomeMap: Record<string, string> = {
  LP: "KeyA",
  LR: "KeyS",
  LM: "KeyD",
  LI: "KeyF",
  LT: "KeyC", // 左大拇指默认定位在 C 键上
  RT: "KeyN", // 右大拇指默认定位在 N 键上
  RI: "KeyJ",
  RM: "KeyK",
  RR: "KeyL",
  RP: "KeyL", // 右小指默认停靠在 L 键上，与其右侧邻近位置完美配合
};

// 键盘坐标定位系统
const keyCoords: Record<string, { r: number; c: number }> = {
  Backquote: { r: 0, c: 0 }, Digit1: { r: 0, c: 1 }, Digit2: { r: 0, c: 2 }, Digit3: { r: 0, c: 3 }, Digit4: { r: 0, c: 4 },
  Digit5: { r: 0, c: 5 }, Digit6: { r: 0, c: 6 }, Digit7: { r: 0, c: 7 }, Digit8: { r: 0, c: 8 }, Digit9: { r: 0, c: 9 },
  Digit0: { r: 0, c: 10 }, Minus: { r: 0, c: 11 }, Equal: { r: 0, c: 12 }, Backspace: { r: 0, c: 13 },

  Tab: { r: 1, c: 0 }, KeyQ: { r: 1, c: 1 }, KeyW: { r: 1, c: 2 }, KeyE: { r: 1, c: 3 }, KeyR: { r: 1, c: 4 },
  KeyT: { r: 1, c: 5 }, KeyY: { r: 1, c: 6 }, KeyU: { r: 1, c: 7 }, KeyI: { r: 1, c: 8 }, KeyO: { r: 1, c: 9 },
  KeyP: { r: 1, c: 10 }, BracketLeft: { r: 1, c: 11 }, BracketRight: { r: 1, c: 12 }, Backslash: { r: 1, c: 13 },

  CapsLock: { r: 2, c: 0 }, KeyA: { r: 2, c: 1 }, KeyS: { r: 2, c: 2 }, KeyD: { r: 2, c: 3 }, KeyF: { r: 2, c: 4 },
  KeyG: { r: 2, c: 5 }, KeyH: { r: 2, c: 6 }, KeyJ: { r: 2, c: 7 }, KeyK: { r: 2, c: 8 }, KeyL: { r: 2, c: 9 },
  Semicolon: { r: 2, c: 10 }, Quote: { r: 2, c: 11 }, Enter: { r: 2, c: 12 },

  ShiftLeft: { r: 3, c: 0 }, KeyZ: { r: 3, c: 1 }, KeyX: { r: 3, c: 2 }, KeyC: { r: 3, c: 3 }, KeyV: { r: 3, c: 4 },
  KeyB: { r: 3, c: 5 }, KeyN: { r: 3, c: 6 }, KeyM: { r: 3, c: 7 }, Comma: { r: 3, c: 8 }, Period: { r: 3, c: 9 },
  Slash: { r: 3, c: 10 }, ShiftRight: { r: 3, c: 11 },

  Space: { r: 4, c: 6 },
};

type ShiftOverride = {
  finger: string;
  hand: "left" | "right";
  rowsDelta?: number;
  colsDelta?: number;
  anchorKey?: string;
};

const shiftOverrides: Record<string, ShiftOverride> = {
  Backquote: { finger: "LP", hand: "left", rowsDelta: -0.3, colsDelta: -0.8 },
  Digit1: { finger: "LP", hand: "left", rowsDelta: -0.35, colsDelta: -0.8 },
  Digit2: { finger: "LR", hand: "left", rowsDelta:  -0.2, colsDelta: -0.7 },
  Digit3: { finger: "LM", hand: "left", rowsDelta: 0.05, colsDelta: -0.8 },
  Digit4: { finger: "LI", hand: "left", rowsDelta: -0.22, colsDelta: -0.85 },
  Digit5: { finger: "LI", hand: "left", rowsDelta: -0.22, colsDelta: -0.85 },
  Digit6: { finger: "RI", hand: "right", rowsDelta: -0.22, colsDelta: -0.75 },
  Digit7: { finger: "RI", hand: "right", rowsDelta: -0.22, colsDelta: -0.75 },
  Digit8: { finger: "RM", hand: "right", rowsDelta: 0.05, colsDelta: -0.8 },
  Digit9: { finger: "RR", hand: "right", rowsDelta:  -0.15, colsDelta: -0.85 },
  Digit0: { finger: "RP", hand: "right", rowsDelta: -0.35, colsDelta: -1.7 },
  Minus: { finger: "RP", hand: "right", rowsDelta: -0.2, colsDelta: -1.67 },
  Equal: { finger: "RP", hand: "right", rowsDelta: -0.3, colsDelta: -1.65 },
  Tab: { finger: "LP", hand: "left", rowsDelta: -0.2, colsDelta: -0.5 },
  KeyA: { finger: "LP", hand: "left", rowsDelta: -0.24, colsDelta: 0 },
  KeyB: { finger: "LI", hand: "left", anchorKey: "KeyD", rowsDelta: -0.12, colsDelta: -0.55 },
  KeyC: { finger: "LM", hand: "left", rowsDelta: 0.12, colsDelta: 0.5 },
  CapsLock: { finger: "LP", hand: "left", rowsDelta: -0.05, colsDelta: -1.3 },
  KeyD: { finger: "LM", hand: "left", rowsDelta: 0.12, colsDelta: 0 },
  KeyE: { finger: "LM", hand: "left", rowsDelta: 0.1, colsDelta: -0.3 },
  KeyF: { finger: "LI", hand: "left", rowsDelta: -0.15, colsDelta: -0.08 },
  KeyG: { finger: "LI", hand: "left", rowsDelta: -0.12, colsDelta: -0.05 },
  KeyH: { finger: "RI", hand: "right", rowsDelta: -0.12, colsDelta:  0.05 },
  KeyI: { finger: "RM", hand: "right", rowsDelta: 0.12, colsDelta: -0.3 },
  KeyJ: { finger: "RI", hand: "right", rowsDelta: -0.12, colsDelta:  0.05 },
  KeyK: { finger: "RM", hand: "right", rowsDelta: 0.12, colsDelta: 0 },
  KeyL: { finger: "RR", hand: "right", rowsDelta:  -0.12, colsDelta: -0.1 },
  KeyM: { finger: "RI", hand: "right", rowsDelta: -0.12, colsDelta: 0.6 },
  KeyN: { finger: "RI", hand: "right", rowsDelta: -0.12, colsDelta: 0.55 },
  KeyO: { finger: "RR", hand: "right", rowsDelta: -0.12, colsDelta: -0.4 },
  KeyP: { finger: "RP", hand: "right", rowsDelta: -0.3, colsDelta: -1.15 },
  BracketLeft: { finger: "RP", hand: "right", rowsDelta: -0.35, colsDelta: -1.15 },
  BracketRight: { finger: "RP", hand: "right", rowsDelta: -0.35, colsDelta: -1.15 },
  Backslash: { finger: "RP", hand: "right", rowsDelta: -0.35, colsDelta: -0.9 },
  KeyQ: { finger: "LP", hand: "left", rowsDelta: -0.3, colsDelta: -0.3 },
  KeyR: { finger: "LI", hand: "left", rowsDelta: -0.2, colsDelta: -0.35 },
  KeyS: { finger: "LR", hand: "left", rowsDelta: -0.1, colsDelta: 0.1 },
  Semicolon: { finger: "RP", hand: "right", rowsDelta: -0.3, colsDelta: -0.9 },
  Quote: { finger: "RP", hand: "right", rowsDelta: -0.35, colsDelta: -0.9 },
  KeyT: { finger: "LI", hand: "left", rowsDelta: -0.2, colsDelta: -0.35 },
  KeyU: { finger: "RI", hand: "right", rowsDelta: -0.2, colsDelta: -0.25 },
  KeyV: { finger: "LI", hand: "left", rowsDelta: -0.2, colsDelta: 0.45 },
  KeyW: { finger: "LR", hand: "left", rowsDelta: -0.15, colsDelta: -0.2 },
  KeyX: { finger: "LR", hand: "left", rowsDelta: -0.1, colsDelta: 0.6 },
  KeyY: { finger: "RI", hand: "right", rowsDelta: -0.2, colsDelta: -0.25 },
  KeyZ: { finger: "LP", hand: "left", rowsDelta: -0.3, colsDelta: 0.5 },
  Enter: { finger: "RP", hand: "right", rowsDelta: -0.12, colsDelta: 2.35 },
  Space: { finger: "RT", hand: "right", rowsDelta: -0.5, colsDelta: 0.1 },
  ShiftLeft: { finger: "LP", hand: "left", rowsDelta: -0.2, colsDelta: 0 },
  Comma: { finger: "RM", hand: "right", rowsDelta: 0.05, colsDelta: 0.5 },
  Period: { finger: "RR", hand: "right", rowsDelta: -0.12, colsDelta: 0.4 },
  Slash: { finger: "RP", hand: "right", rowsDelta: -0.35, colsDelta: -0.35 },
  ShiftRight: { finger: "RP", hand: "right", rowsDelta: -0.3, colsDelta: 0.15 },
  Backspace: { finger: "RP", hand: "right", rowsDelta: -0.28, colsDelta: -1 },
};

const defaultKeys: Key[][] = [
  [
    { label: "` ~", code: "Backquote", color: "#d7dde824" },
    { label: "1 !", code: "Digit1", color: "#d7dde824" },
    { label: "2 @", code: "Digit2", color: "#d7dde824" },
    { label: "3 #", code: "Digit3", color: "#d7dde824" },
    { label: "4 $", code: "Digit4", color: "#d7dde824" },
    { label: "5 %", code: "Digit5", color: "#d7dde824" },
    { label: "6 ^", code: "Digit6", color: "#d7dde824" },
    { label: "7 &", code: "Digit7", color: "#d7dde824" },
    { label: "8 *", code: "Digit8", color: "#d7dde824" },
    { label: "9 (", code: "Digit9", color: "#d7dde824" },
    { label: "0 )", code: "Digit0", color: "#d7dde824" },
    { label: "- _", code: "Minus", color: "#d7dde824" },
    { label: "= +", code: "Equal", color: "#d7dde824" },
    { label: "Backspace", code: "Backspace", color: "#d7dde824", width: 2 },
  ],
  [
    { label: "Tab", code: "Tab", color: "#e6ebf524", width: 1.5 },
    { label: "Q", code: "KeyQ", color: "#0057ff24" },
    { label: "W", code: "KeyW", color: "#ff00f21f" },
    { label: "E", code: "KeyE", color: "#ffcb0087" },
    { label: "R", code: "KeyR", color: "#a3fb6c3d" },
    { label: "T", code: "KeyT", color: "#a3fb6c3d" },
    { label: "Y", code: "KeyY", color: "#d641411f" },
    { label: "U", code: "KeyU", color: "#d641411f" },
    { label: "I", code: "KeyI", color: "#5febdc33" },
    { label: "O", code: "KeyO", color: "#ebd55f33" },
    { label: "P", code: "KeyP", color: "#b25feb33" },
    { label: "[ {", code: "BracketLeft", color: "#e6ebf524" },
    { label: "] }", code: "BracketRight", color: "#e6ebf524" },
    { label: "\\ |", code: "Backslash", color: "#e6ebf524", width: 1.5 },
  ],
  [
    { label: "Caps", code: "CapsLock", color: "#e6ebf524", width: 1.8 },
    { label: "A", code: "KeyA", color: "#0057ff24" },
    { label: "S", code: "KeyS", color: "#ff00f21f" },
    { label: "D", code: "KeyD", color: "#ffcb0087" },
    { label: "F", code: "KeyF", color: "#a3fb6c3d" },
    { label: "G", code: "KeyG", color: "#a3fb6c3d" },
    { label: "H", code: "KeyH", color: "#d641411f" },
    { label: "J", code: "KeyJ", color: "#d641411f" },
    { label: "K", code: "KeyK", color: "#5febdc33" },
    { label: "L", code: "KeyL", color: "#ebd55f33" },
    { label: "; :", code: "Semicolon", color: "#e6ebf524" },
    { label: "' \"", code: "Quote", color: "#e6ebf524" },
    { label: "Enter", code: "Enter", color: "#e6ebf524", width: 2.2 },
  ],
  [
    { label: "Shift", code: "ShiftLeft", color: "#e6ebf524", width: 2.3 },
    { label: "Z", code: "KeyZ", color: "#0057ff24" },
    { label: "X", code: "KeyX", color: "#ff00f21f" },
    { label: "C", code: "KeyC", color: "#ffcb0087" },
    { label: "V", code: "KeyV", color: "#a3fb6c3d" },
    { label: "B", code: "KeyB", color: "#a3fb6c3d" },
    { label: "N", code: "KeyN", color: "#d641411f" },
    { label: "M", code: "KeyM", color: "#d641411f" },
    { label: ", <", code: "Comma", color: "#e6ebf524" },
    { label: ". >", code: "Period", color: "#e6ebf524" },
    { label: "/ ?", code: "Slash", color: "#e6ebf524" },
    { label: "Shift", code: "ShiftRight", color: "#e6ebf524", width: 2.7 },
  ],
  [
    { label: "Space", code: "Space", color: "#eef2f824", width: 8 },
  ],
];

const defaultKeyLookup = new Map(defaultKeys.flat().map((key) => [key.code, key]));

const directKeyboardLayout: Record<string, Omit<KeyStroke, "shiftCode">> = {
  a: { code: "KeyA", display: "A" },
  b: { code: "KeyB", display: "B" },
  c: { code: "KeyC", display: "C" },
  d: { code: "KeyD", display: "D" },
  e: { code: "KeyE", display: "E" },
  f: { code: "KeyF", display: "F" },
  g: { code: "KeyG", display: "G" },
  h: { code: "KeyH", display: "H" },
  i: { code: "KeyI", display: "I" },
  j: { code: "KeyJ", display: "J" },
  k: { code: "KeyK", display: "K" },
  l: { code: "KeyL", display: "L" },
  m: { code: "KeyM", display: "M" },
  n: { code: "KeyN", display: "N" },
  o: { code: "KeyO", display: "O" },
  p: { code: "KeyP", display: "P" },
  q: { code: "KeyQ", display: "Q" },
  r: { code: "KeyR", display: "R" },
  s: { code: "KeyS", display: "S" },
  t: { code: "KeyT", display: "T" },
  u: { code: "KeyU", display: "U" },
  v: { code: "KeyV", display: "V" },
  w: { code: "KeyW", display: "W" },
  x: { code: "KeyX", display: "X" },
  y: { code: "KeyY", display: "Y" },
  z: { code: "KeyZ", display: "Z" },
  "1": { code: "Digit1", display: "1" },
  "2": { code: "Digit2", display: "2" },
  "3": { code: "Digit3", display: "3" },
  "4": { code: "Digit4", display: "4" },
  "5": { code: "Digit5", display: "5" },
  "6": { code: "Digit6", display: "6" },
  "7": { code: "Digit7", display: "7" },
  "8": { code: "Digit8", display: "8" },
  "9": { code: "Digit9", display: "9" },
  "0": { code: "Digit0", display: "0" },
  "`": { code: "Backquote", display: "`" },
  "~": { code: "Backquote", shift: true, display: "~" },
  "-": { code: "Minus", display: "-" },
  _: { code: "Minus", shift: true, display: "_" },
  "=": { code: "Equal", display: "=" },
  "+": { code: "Equal", shift: true, display: "+" },
  "[": { code: "BracketLeft", display: "[" },
  "{": { code: "BracketLeft", shift: true, display: "{" },
  "]": { code: "BracketRight", display: "]" },
  "}": { code: "BracketRight", shift: true, display: "}" },
  "\\": { code: "Backslash", display: "\\" },
  "|": { code: "Backslash", shift: true, display: "|" },
  ";": { code: "Semicolon", display: ";" },
  ":": { code: "Semicolon", shift: true, display: ":" },
  "'": { code: "Quote", display: "'" },
  '"': { code: "Quote", shift: true, display: '"' },
  ",": { code: "Comma", display: "," },
  "<": { code: "Comma", shift: true, display: "<" },
  ".": { code: "Period", display: "." },
  ">": { code: "Period", shift: true, display: ">" },
  "/": { code: "Slash", display: "/" },
  "?": { code: "Slash", shift: true, display: "?" },
  "!": { code: "Digit1", shift: true, display: "!" },
  "@": { code: "Digit2", shift: true, display: "@" },
  "#": { code: "Digit3", shift: true, display: "#" },
  $: { code: "Digit4", shift: true, display: "$" },
  "%": { code: "Digit5", shift: true, display: "%" },
  "^": { code: "Digit6", shift: true, display: "^" },
  "&": { code: "Digit7", shift: true, display: "&" },
  "*": { code: "Digit8", shift: true, display: "*" },
  "(": { code: "Digit9", shift: true, display: "(" },
  ")": { code: "Digit0", shift: true, display: ")" },
  " ": { code: "Space", display: "空格" },
  "\u3000": { code: "Space", display: "空格" },
  "\n": { code: "Enter", display: "Enter" },
  "\t": { code: "Tab", display: "Tab" },
  "，": { code: "Comma", display: "，" },
  "。": { code: "Period", display: "。" },
  "、": { code: "Slash", display: "、" },
  "；": { code: "Semicolon", display: "；" },
  "：": { code: "Semicolon", shift: true, display: "：" },
  "？": { code: "Slash", shift: true, display: "？" },
  "！": { code: "Digit1", shift: true, display: "！" },
  "“": { code: "Quote", shift: true, display: "“" },
  "”": { code: "Quote", shift: true, display: "”" },
  "‘": { code: "Quote", display: "‘" },
  "’": { code: "Quote", display: "’" },
  "（": { code: "Digit9", shift: true, display: "（" },
  "）": { code: "Digit0", shift: true, display: "）" },
  "【": { code: "BracketLeft", display: "【" },
  "】": { code: "BracketRight", display: "】" },
  "《": { code: "Comma", shift: true, display: "《" },
  "》": { code: "Period", shift: true, display: "》" },
  "…": { code: "Digit6", shift: true, display: "…" },
  "—": { code: "Minus", shift: true, display: "—" },
  "·": { code: "Backquote", display: "·" },
  "￥": { code: "Digit4", shift: true, display: "￥" },
};

const specialLetterOverrides: Record<string, Omit<KeyStroke, "shiftCode">> = {
  ü: { code: "KeyV", display: "Ü" },
  ǖ: { code: "KeyV", display: "Ǖ" },
  ǘ: { code: "KeyV", display: "Ǘ" },
  ǚ: { code: "KeyV", display: "Ǚ" },
  ǜ: { code: "KeyV", display: "Ǜ" },
};

const getShiftCodeForKey = (code: string): "ShiftLeft" | "ShiftRight" => {
  const finger = keyToFingerMap[code];
  if (!finger) {
    return "ShiftLeft";
  }

  return leftFingers.includes(finger) ? "ShiftRight" : "ShiftLeft";
};

const buildKeyStroke = (stroke: Omit<KeyStroke, "shiftCode">): KeyStroke => {
  if (!stroke.shift) {
    return stroke;
  }

  return {
    ...stroke,
    shiftCode: getShiftCodeForKey(stroke.code),
  };
};

const getKeyLabelByCode = (code: string) => {
  return defaultKeyLookup.get(code)?.label || code.replace(/^Key/, "").replace(/^Digit/, "");
};

const isUppercaseLetter = (char: string) => {
  return char !== char.toLowerCase() && char === char.toUpperCase();
};

const isLetterKeyCode = (code: string) => {
  return /^Key[A-Z]$/.test(code);
};

export function stringToKeyboardKeys(input: string): KeyStroke[] {
  const keys: KeyStroke[] = [];

  for (const char of input) {
    const directMatch = directKeyboardLayout[char];
    if (directMatch) {
      keys.push(buildKeyStroke(directMatch));
      continue;
    }

    const override = specialLetterOverrides[char.toLowerCase()];
    if (override) {
      keys.push(
        buildKeyStroke({
          ...override,
          shift: isUppercaseLetter(char),
          display: char,
        })
      );
      continue;
    }

    const normalizedChar = char.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const lowerChar = normalizedChar.toLowerCase();
    const normalizedMatch = directKeyboardLayout[lowerChar];
    if (normalizedMatch && /^Key[A-Z]$/.test(normalizedMatch.code)) {
      keys.push(
        buildKeyStroke({
          ...normalizedMatch,
          shift: isUppercaseLetter(char),
          display: char,
        })
      );
      continue;
    }

    keys.push({
      code: char,
      display: char,
    });
  }

  return keys;
}

const normalizeTargetKeyStroke = (targetKeyCode?: KeyStroke | string) => {
  if (!targetKeyCode) {
    return null;
  }

  if (typeof targetKeyCode !== "string") {
    return targetKeyCode;
  }

  if (defaultKeyLookup.has(targetKeyCode)) {
    return {
      code: targetKeyCode,
      display: getKeyLabelByCode(targetKeyCode),
    } as KeyStroke;
  }

  return stringToKeyboardKeys(targetKeyCode)[0] || null;
};

const isShiftCode = (code: string): code is ShiftCode => {
  return code === "ShiftLeft" || code === "ShiftRight";
};

const matchesTargetStrokeWithHeldShift = (
  targetStroke: KeyStroke | null,
  code: string,
  heldShiftCodes: readonly ShiftCode[]
) => {
  if (!targetStroke) {
    return null;
  }

  if (targetStroke.code !== code) {
    return false;
  }

  if (!targetStroke.shift) {
    return true;
  }

  if (!targetStroke.shiftCode) {
    return heldShiftCodes.length > 0;
  }

  return heldShiftCodes.includes(targetStroke.shiftCode);
};

const applyCapsLockToTargetStroke = (
  targetStroke: KeyStroke | null,
  isCapsLockOn: boolean
): KeyStroke | null => {
  if (!targetStroke || !isCapsLockOn || !isLetterKeyCode(targetStroke.code)) {
    return targetStroke;
  }

  const needsShift = !Boolean(targetStroke.shift);

  if (!needsShift) {
    const { shift, shiftCode, ...strokeWithoutShift } = targetStroke;
    return strokeWithoutShift;
  }

  return {
    ...targetStroke,
    shift: true,
    shiftCode: targetStroke.shiftCode || getShiftCodeForKey(targetStroke.code),
  };
};

const getKeyWidthStyle = (width?: number): React.CSSProperties => {
  if (!width || width === 1) {
    return {};
  }

  return {
    minWidth: `calc(var(--key-stride, 48px) * ${width} - 8px)`,
    width: `calc(var(--key-stride, 48px) * ${width} - 8px)`,
  };
};

const getKeyLabelParts = (label: string) => {
  const parts = label.split(" ").filter(Boolean);

  if (parts.length >= 2) {
    return {
      top: parts[1],
      bottom: parts[0],
    };
  }

  return {
    top: "",
    bottom: label,
  };
};

const getHandShiftForCode = (code?: string) => {
  if (!code || !keyToFingerMap[code]) {
    return null;
  }

  const responsibleFinger = keyToFingerMap[code];
  const homeKey = fingerHomeMap[responsibleFinger];

  if (!homeKey || !keyCoords[code] || !keyCoords[homeKey]) {
    return null;
  }

  const tgtCoord = keyCoords[code];
  const homeCoord = keyCoords[homeKey];

  let rows = tgtCoord.r - homeCoord.r;
  let cols = tgtCoord.c - homeCoord.c;

  const override = shiftOverrides[code];
  if (override && override.finger === responsibleFinger) {
    if (override.anchorKey && keyCoords[override.anchorKey]) {
      const anchorCoord = keyCoords[override.anchorKey];
      rows = tgtCoord.r - anchorCoord.r;
      cols = tgtCoord.c - anchorCoord.c;
    }

    rows += override.rowsDelta ?? 0;
    cols += override.colsDelta ?? 0;
  }

  return {
    finger: responsibleFinger,
    hand: leftFingers.includes(responsibleFinger) ? "left" : "right",
    rows,
    cols,
  } as const;
};

// 渲染大方高科技感、只要一个边框的极简 SVG 手指
const renderFingerSVG = (finger: string, isHighlight: boolean, shiftCols = 0, shiftRows = 0) => {
  return (
    <div 
      key={finger} 
      className={`finger-container finger-${finger.toLowerCase()} ${isHighlight ? "active" : ""}`}
      style={{
        "--shift-cols": shiftCols,
        "--shift-rows": shiftRows,
      } as React.CSSProperties}
    >
      <svg 
        className="finger-svg"
        viewBox="0 0 30 160"
        width="30"
        height="160"
      >
        {/* 精美极简大方的纯透明边框手指主体 */}
        <path
          d="M5 150 L5 25 C5 10, 25 10, 25 25 L25 150"
          fill="none"
          stroke={isHighlight ? fingerHighlightColor : "rgba(61, 61, 61, 0.35)"}
          strokeWidth={isHighlight ? "2.5" : "1.8"}
        />
      </svg>
    </div>
  );
};

const Keyboard: React.FC<KeyboardProps> = ({
  value,
  height,
  onKeyPress,
  onChange,
  onComposingChnage,
  onHeightChange,
  onInit,
  onFocus,
  onBlur,
  targetKeyCode,
  availableKeyCodes = [],
  showKeyboard = true, // 是否显示键盘
  showAvailableKeys = false, // 是否显示可用按键范围
  showTargetKey = false, // 是否显示当前需要按下的键
}) => {
  const [isComposing, setIsComposing] = useState(false);
  const [text, setInputText] = useState(value);
  const ref = React.useRef<HTMLTextAreaElement>(null);

  const [isCapsLockOn, setIsCapsLockOn] = useState(false);
  const [activeKey, setActiveKey] = useState<Key | null>(null);
  const [heldShiftCodes, setHeldShiftCodes] = useState<ShiftCode[]>([]);
  const [lastPressedKey, setLastPressedKey] = useState<{
    key: Key;
    isCorrect: boolean | null;
  } | null>(null);
  const heldShiftCodesRef = React.useRef<ShiftCode[]>([]);

  useEffect(() => {
    if (lastPressedKey) {
      const timeout = setTimeout(() => {
        setLastPressedKey(null);
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [lastPressedKey]);

  useEffect(() => {
    setInputText(value);
  }, [value]);

  useEffect(() => {
    if (ref.current) {
      onInit?.((ref.current as any).resizableTextArea.textArea); 
    }
  }, [ref, onInit]);

  const rawTargetStroke = useMemo(() => {
    return normalizeTargetKeyStroke(targetKeyCode);
  }, [targetKeyCode]);

  const targetStroke = useMemo(() => {
    return applyCapsLockToTargetStroke(rawTargetStroke, isCapsLockOn);
  }, [rawTargetStroke, isCapsLockOn]);

  const targetCodes = useMemo(() => {
    return new Set(
      [targetStroke?.code, targetStroke?.shiftCode].filter(
        (code): code is string => Boolean(code)
      )
    );
  }, [targetStroke]);

  const isTargetShiftPrimed = useMemo(() => {
    if (!targetStroke?.shift || !targetStroke.shiftCode) {
      return false;
    }

    return heldShiftCodes.includes(targetStroke.shiftCode);
  }, [heldShiftCodes, targetStroke]);

  // 计算两只手的整体运动偏移 (一指触，整手动)
  const handShifts = useMemo(() => {
    let leftShift = { rows: 0, cols: 0 };
    let rightShift = { rows: 0, cols: 0 };

    const primaryCode = activeKey?.code || targetStroke?.code;
    const primaryShift = getHandShiftForCode(primaryCode);
    if (primaryShift) {
      if (primaryShift.hand === "left") {
        leftShift = { rows: primaryShift.rows, cols: primaryShift.cols };
      } else {
        rightShift = { rows: primaryShift.rows, cols: primaryShift.cols };
      }
    }

    const shiftHandCode = targetStroke?.shiftCode;
    const shiftHandShift = getHandShiftForCode(shiftHandCode);
    if (shiftHandShift) {
      if (shiftHandShift.hand === "left") {
        leftShift = { rows: shiftHandShift.rows, cols: shiftHandShift.cols };
      } else {
        rightShift = { rows: shiftHandShift.rows, cols: shiftHandShift.cols };
      }
    }

    return { leftShift, rightShift };
  }, [targetStroke, activeKey]);

  // 计算十个手指的实时位置与高亮状态（需要时手部整体运动，平时在各自 Home 键安静守候）
  const fingerStatus = useMemo(() => {
    const hasActiveKey = Boolean(activeKey);

    return FINGERS.map((finger) => {
      // 该手指专门负责的键位范围
      const responsibleKeys = Object.keys(keyToFingerMap).filter(
        (key) => keyToFingerMap[key] === finger
      );

      const destinationKey = fingerHomeMap[finger] || "";
      let isHighlight = false;

      if (hasActiveKey) {
        if (activeKey && responsibleKeys.includes(activeKey.code)) {
          isHighlight = true;
        }
        else if (finger === "RT" && activeKey?.code === "Space") {
          isHighlight = true;
        }
      } else {
        // 没有实际按键输入时，才退回到目标提示高亮
        if (Array.from(targetCodes).some((code) => responsibleKeys.includes(code))) {
          isHighlight = true;
        }
        else if (finger === "RT" && targetStroke?.code === "Space") {
          isHighlight = true;
        }
      }

      // 获取当前手部的偏移步数
      const isLeft = leftFingers.includes(finger);
      const shift = isLeft ? handShifts.leftShift : handShifts.rightShift;

      return { 
        finger, 
        destinationKey, 
        isHighlight,
        shiftCols: shift.cols,
        shiftRows: shift.rows,
      };
    });
  }, [targetCodes, targetStroke, activeKey, handShifts]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    setIsCapsLockOn(e.getModifierState("CapsLock"));

    const key = defaultKeyLookup.get(e.code);
    if (key) {
      let nextHeldShiftCodes = heldShiftCodesRef.current;
      if (isShiftCode(key.code) && !nextHeldShiftCodes.includes(key.code)) {
        nextHeldShiftCodes = [...nextHeldShiftCodes, key.code];
        heldShiftCodesRef.current = nextHeldShiftCodes;
        setHeldShiftCodes(nextHeldShiftCodes);
      }

      // 检查是否在可用范围内
      const isAvailable =
        availableKeyCodes.length === 0 || availableKeyCodes.includes(key.code);

      if (isAvailable) {
        setActiveKey(key);

        const isPreparingTargetShift =
          Boolean(targetStroke?.shiftCode && targetStroke.shiftCode === key.code);

        if (isPreparingTargetShift) {
          setLastPressedKey({ key, isCorrect: null });
          return;
        }

        // 检查是否是目标按键（如果有设置）
        const isCorrect = matchesTargetStrokeWithHeldShift(
          targetStroke,
          key.code,
          nextHeldShiftCodes
        );
        setLastPressedKey({ key, isCorrect });

        onKeyPress(key, isCorrect);
      }
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    setIsCapsLockOn(e.getModifierState("CapsLock"));

    if (isShiftCode(e.code)) {
      const nextHeldShiftCodes = heldShiftCodesRef.current.filter(
        (code) => code !== e.code
      );
      heldShiftCodesRef.current = nextHeldShiftCodes;
      setHeldShiftCodes(nextHeldShiftCodes);
    }

    setActiveKey(null);
  };

  const handleFocusInternal = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    const capsLockState = (e.nativeEvent as { getModifierState?: (key: string) => boolean })
      .getModifierState?.("CapsLock");
    if (typeof capsLockState === "boolean") {
      setIsCapsLockOn(capsLockState);
    }
    onFocus?.();
  };

  const handleBlur = () => {
    heldShiftCodesRef.current = [];
    setHeldShiftCodes([]);
    setActiveKey(null);
    onBlur?.();
  };

  return (
    <div className="keyboard">
      <ResizableTextarea
        ref={ref}
        className="textarea"
        size="large"
        value={text}
        defaultHeight={height}
        autoFocus
        rows={2}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onFocus={handleFocusInternal}
        onBlur={handleBlur}
        onSizeChange={(height) => onHeightChange?.(height)}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={(e) => {
          setIsComposing(false);
          onChange(text); // 确保在输入法结束时更新文本
        }}
        onChange={(e) => {
          if (isComposing) {
            setInputText(e.target.value);
            onComposingChnage?.(e.target.value);
          } else {
            onChange(e.target.value);
          }
        }}
      ></ResizableTextarea>

      {/* 显示当前需要按下的键（如果有设置） */}
      {showTargetKey && targetStroke && (
        <div className="target-key-indicator">
          请按下:{" "}
          <span style={{ color: "#f44336", fontWeight: "bold" }}>
            {targetStroke.shift
              ? isTargetShiftPrimed
                ? `保持 Shift 按住，再按 ${targetStroke.display}`
                : `先按住 Shift，再按 ${targetStroke.display}`
              : targetStroke.display}
          </span>
        </div>
      )}

      {/* 显示可用按键范围（如果有设置） */}
      {showAvailableKeys && availableKeyCodes.length > 0 && (
        <div className="available-keys-indicator">
          可用按键:{" "}
          {availableKeyCodes
            .map((code) => getKeyLabelByCode(code))
            .join(", ")}
        </div>
      )}

      {showKeyboard && (
        <div className="keyboard-container-relative" style={{ position: "relative" }}>
          {/* 1. 真实的按键交互层 */}
          {defaultKeys.map((row, rowIndex) => {
            const isSpaceRow = row.some((item) => item.code === "Space");

            return (
            <div
              key={rowIndex}
              className={`keyboard-row ${isSpaceRow ? "keyboard-row-space" : ""}`}
            >
              {row.map((key, keyIndex) => {
                const labelParts = getKeyLabelParts(key.label);
                // 判断按键状态
                const isTargetKey = targetCodes.has(key.code);
                const isActive = activeKey?.code === key.code;
                const isLastPressed = lastPressedKey?.key.code === key.code;
                const isCorrect =
                  isLastPressed && lastPressedKey.isCorrect === true;
                const isWrong =
                  isLastPressed && lastPressedKey.isCorrect === false;
                const isAvailable =
                  availableKeyCodes.length === 0 ||
                  availableKeyCodes.includes(key.code);

                // 计算背景颜色
                let backgroundColor = key.color || "#e0e0e0";
                if (!isAvailable) backgroundColor = "#bdbdbd";
                else if (isActive) backgroundColor = key.color || "#ffcc80";
                else if (isCorrect) backgroundColor = "#fefefe";
                else if (isWrong) backgroundColor = "#fefefe";

                // 计算动画类
                let animationClass = "";
                if (isLastPressed) animationClass = "press-flash-animation";
                if (isCorrect) animationClass = "correct-animation";
                if (isWrong) animationClass = "wrong-animation";

                return (
                  <button
                    key={keyIndex}
                    className={`key ${animationClass} ${
                      isTargetKey ? "target-key" : ""
                    }`}
                    style={{
                      ...getKeyWidthStyle(key.width),
                      backgroundColor,
                      borderColor: isTargetKey || isActive ? fingerHighlightColor : undefined,
                    }}
                    onClick={() => {
                      if (isAvailable) {
                        const isCorrect = targetStroke ? targetCodes.has(key.code) : null;
                        setLastPressedKey({ key, isCorrect });
                        onKeyPress(key, isCorrect);
                      }
                    }}
                    disabled={!isAvailable}
                  >
                    <span
                      className={`key-label ${
                        labelParts.top ? "key-label-dual" : "key-label-single"
                      }`}
                    >
                      {labelParts.top && (
                        <span className="key-label-top">{labelParts.top}</span>
                      )}
                      <span className="key-label-bottom">{labelParts.bottom}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )})}

          {/* 2. 独立的手指半透明浮层 (脱离按键及文本框，永不干扰交互) */}
          <div className="keyboard-fingers-layer" style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 10, overflow: "visible" }}>
            {defaultKeys
              .filter((row) => !row.some((item) => item.code === "Space"))
              .map((row, rowIndex) => (
              <div key={rowIndex} className="keyboard-row">
                {row.map((key, keyIndex) => {
                  const fingersOnThisKey = fingerStatus.filter(
                    (fs) => fs.destinationKey === key.code
                  );
                  return (
                    <div
                      key={keyIndex}
                      className="key placeholder-only"
                      style={getKeyWidthStyle(key.width)}
                    >
                      {fingersOnThisKey.map((fs) =>
                        renderFingerSVG(fs.finger, fs.isHighlight, fs.shiftCols, fs.shiftRows)
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Keyboard;
